import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  TrendingUp,
  ShieldCheck,
  Repeat,
  Database,
  Sparkles,
  Calculator,
  Banknote,
  LineChart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SidebPill } from "@/components/SidebPill";
import { ProofTldr } from "@/components/ProofTldr";
import CaseStudyFaq from "@/components/CaseStudyFaq";
import { openChat } from "@/lib/chat";
import { useHashScroll } from "@/hooks/useHashScroll";
import { Seo, articleSchema, faqSchema, breadcrumbSchema } from "@/components/Seo";

const FAQS = [
  { q: "What is a Payments Optimization Sprint and who is it for?", a: "A 2-week working sprint for teams ready to act on payment cost and acceptance lift — not just identify the opportunities. Includes a full Payments Diagnostic plus a vendor-renegotiation playbook (target rates, BATNA, scripts), BIN-routing optimization, decline-recovery rules, and a sequenced 90-day execution plan. Built for Heads of Payments, CFOs, and RevOps leaders at fintechs, marketplaces, and PSPs." },
  { q: "What is AFT/OCT compliance and which businesses does it affect?", a: "AFT (Account Funding Transactions) and OCT (Original Credit Transactions) are Visa and Mastercard scheme-level mandates that govern how certain money-movement transactions must be authorized, flagged, and settled. They apply most stringently to specific MCC categories — including money services, gaming, crypto on/off-ramps, and some financial-services use cases — and apply both to domestic and cross-border flows. Falling out of alignment risks scheme penalties, automated declines, and forced repricing on the portfolio. SideB engages with the in-scope orchestration and rule changes to bring the stack into current alignment." },
  { q: "How did SideB lift authorization rates on this engagement?", a: "The 1.5% acceptance lift documented in this case study came from three combined moves: re-sequencing API calls across PSPs and fraud vendors to eliminate redundant decisioning, aligning the stack with current scheme mandates (AFT/OCT for in-scope MCCs, network tokens, smart routing), and adding decline-recovery rules that retry intelligently on soft declines. On the client's nine-figure annual volume, that 1.5% translated to roughly $9M of newly recovered transactional throughput. Outcomes vary by stack, volume, baseline, and scope of work." },
  { q: "How long does a Payments Optimization Sprint take to deliver?", a: "The sprint itself runs 2 weeks. Any vendor renegotiations and orchestration changes the team chooses to execute typically close within the following 30–60 days. Scope and timing of post-sprint execution are owned by the client team." },
];

const KPIS = [
  { value: "30%", label: "PSP & fraud-vendor cost reduction, annually", icon: TrendingUp },
  { value: "+1.5%", label: "Authorization & acceptance lift", icon: LineChart },
  { value: "~$9M", label: "Newly recovered transactional volume, year one", icon: Banknote },
  { value: "1.5x", label: "Return probability per recovered first transaction", icon: Repeat },
];

export default function ProofRevenuePerformance() {
  useHashScroll();

  return (
    <div className="bg-white" data-testid="proof-revenue-performance-page">
      <Seo
        title="How SideB helped a regulated remitter cut PSP costs 30% and lift acceptance 1.5% | SideB Case Study"
        description="Case study: SideB's Payments Optimization Sprint re-engineered the acceptance stack for a regulated nine-figure money services business — 30% PSP & fraud-vendor cost reduction, +1.5% authorization lift, AFT/OCT compliance alignment, and ~$9M of recovered volume in year one."
        canonical="https://sideb.io/proof/revenue-performance"
        ogType="article"
        keywords="Payments Optimization Sprint, authorization rate lift, AFT OCT compliance, PSP cost reduction, decline recovery, BIN routing"
        schema={[
          articleSchema({
            headline: "How SideB helped a regulated remitter cut PSP costs 30% and lift acceptance 1.5%",
            description: "SideB's Payments Optimization Sprint delivered 30% PSP & fraud-vendor cost reduction, +1.5% authorization lift, and ~$9M of recovered volume in year one.",
            slug: "proof/revenue-performance",
            datePublished: "2026-01-01",
            section: "Case Study",
          }),
          faqSchema(FAQS),
          breadcrumbSchema([
            { name: "Home", url: "https://sideb.io/" },
            { name: "Proof", url: "https://sideb.io/proof" },
            { name: "Revenue Performance Case Study", url: "https://sideb.io/proof/revenue-performance" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0a1628] text-white">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          aria-hidden
          style={{
            background:
              "radial-gradient(60% 50% at 25% 30%, rgba(172,98,234,0.45) 0%, rgba(11,30,90,0) 70%)",
            filter: "blur(40px)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Link
            to="/proof"
            className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white tracking-widest uppercase mb-6"
            data-testid="proof-revperf-back-to-hub"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All case studies
          </Link>
          <div className="label-mono text-white/70">Proof · Payments Optimization Sprint · MSB / Remitter</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-[-0.035em] leading-[1.05] font-semibold max-w-4xl">
            A high-volume regulated remitter, AFT/OCT compliance, and a 1.5% lift on every dollar. <span className="text-[#c9a3f4]">Acceptance economics, re-engineered.</span>
          </h1>
          <p className="text-lg text-white/80 mt-6 max-w-2xl leading-relaxed">
            A heavily regulated global Money Services Business processing nine-figure annual remittance volume — caught between bloated PSP and fraud-vendor spend, AFT/OCT compliance pressure on cross-border money movement, and data silos that hid where margin was actually leaking.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="proof-revperf-hero-ctas">
            <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-6">
              <Link to="/services/revenue-performance" data-testid="proof-revperf-cta-service">
                Explore Payments Optimization Sprint <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="btn-pill h-11 px-6 border-white/30 text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/inquiry" data-testid="proof-revperf-cta-brief">Send a brief</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* KPI grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-12 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {KPIS.map((k) => {
            const Icon = k.icon;
            return (
              <div
                key={k.label}
                className="card-sharp p-5 bg-white shadow-[0_18px_40px_-12px_rgba(11,30,90,0.18)]"
                data-testid={`proof-revperf-kpi-${k.label.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
              >
                <div className="w-9 h-9 rounded-lg bg-[#ac62ea]/12 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-[#ac62ea]" />
                </div>
                <div className="font-display text-3xl mt-3 font-semibold tracking-tight">{k.value}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-snug">{k.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TL;DR */}
      <ProofTldr
        testid="proof-revperf-tldr"
        problem="Regulated global money-services business with rising PSP and fraud-vendor costs, AFT/OCT compliance drag, and acceptance economics nobody owned end-to-end."
        approach="Revenue Performance Package — mapped true cost of acceptance, re-engineered API orchestration to align with scheme mandates, unified the view across Finance, Product and Customer Service."
        outcome="30% off PSP & fraud-vendor spend, +1.5% acceptance lift, roughly $9M of previously-declined volume recovered in year one, multi-year LTV flywheel underneath. Outcomes vary by engagement scope."
      />

      {/* Body sections */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 space-y-14">
        {/* Executive Summary */}
        <section data-testid="proof-revperf-summary">
          <div className="label-mono">Executive summary</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">How SideB helped a regulated remitter cut PSP and fraud-vendor costs 30% and lift authorization acceptance 1.5%.</h2>
          <p className="mt-5 text-foreground/85 leading-relaxed text-lg">
            A heavily regulated global Money Services Business — running nine-figure annual remittance volume under cross-border AFT/OCT mandates — was running a payment stack that worked, but worked expensively. PSP and fraud-vendor fees were drifting up renewal after renewal, redundant authorization checks were running in series across vendors, and Visa and Mastercard scheme mandates on cross-border money movement kept tightening.
          </p>
          <p className="mt-4 text-foreground/85 leading-relaxed text-lg">
            Under the <b>Revenue Performance Package</b>, <SidebPill /> mapped the full cost of acceptance, re-engineered the API orchestration to align with scheme mandates, and unified acceptance economics into the hands of Finance, Product, and Customer Service. The result wasn&apos;t a single line item — it was a <b>30% reduction</b> in PSP and fraud-vendor cost, a <b>1.5% lift</b> on the client&apos;s authorization volume, and a customer-retention flywheel that compounds every year forward. Specific outcomes from any future engagement will vary by scope, stack, and baseline.
          </p>
        </section>

        {/* Challenge */}
        <section id="challenge" className="scroll-mt-24" data-testid="proof-revperf-challenge">
          <div className="label-mono">The challenge</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">Navigating compliance and friction in high-volume remittance.</h2>
          <p className="mt-5 text-foreground/85 leading-relaxed text-lg">
            High-volume cross-border remitters don&apos;t fail on a single line. They fail at the seams — between PSPs, between fraud vendors, between scheme mandates and product reality. The client was hitting all three.
          </p>
          <ul className="mt-5 space-y-3 text-foreground/85 leading-relaxed">
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Inefficient API orchestration.</b> Sub-optimal sequencing of API calls across multiple PSPs and fraud-prevention vendors meant the same transaction was hitting redundant decision layers — bloating processing latency, bloating per-transaction cost, and bloating the surface area where things could fail.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>AFT/OCT compliance risk.</b> Card-scheme mandates for <b>Account Funding Transactions (AFTs)</b> and <b>Original Credit Transactions (OCTs)</b> shift on a multi-year cadence and apply with extra severity to money-movement businesses. Falling out of alignment risks scheme penalties, automated declines, and forced repricing on the entire portfolio.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Data silos across the org.</b> Finance owned the cost picture, Product owned the funnel picture, Customer Service owned the dispute picture — and none of them owned a unified view. Troubleshooting a failed transaction meant cross-team archeology; serving a customer on a decline meant guesswork.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Compounding vendor fees.</b> Multi-year PSP and fraud-vendor contracts had drifted into a &ldquo;stacked&rdquo; cost structure where overlapping coverage produced overlapping fees — without anyone individually owning the total cost-of-acceptance number.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Margin invisible.</b> Without a unified cost-of-acceptance dashboard, the team couldn&apos;t see which corridors, BINs, or customer segments were carrying the unprofitable load — so they couldn&apos;t price, route, or risk-manage around it.</span></li>
          </ul>
        </section>

        {/* Solution */}
        <section id="solution" className="scroll-mt-24" data-testid="proof-revperf-solution">
          <div className="label-mono">The solution · Revenue Performance Package</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">Precision orchestration and compliance alignment.</h2>
          <p className="mt-5 text-foreground/85 leading-relaxed text-lg">
            The Revenue Performance Package isn&apos;t a tooling project — it&apos;s a sustained audit of how every dollar moves through the acceptance stack, paired with the operating capability to fix what the audit surfaces. <SidebPill /> ran three parallel workstreams.
          </p>
          <ol className="mt-6 space-y-4 text-foreground/85 leading-relaxed">
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">01</span> <span><b>Technical optimization · API re-orchestration.</b> Audited and restructured the full digital payment acceptance architecture — collapsed redundant decision layers across PSPs and fraud vendors, re-sequenced API calls into a single coherent flow, and removed the per-transaction tax of running parallel checks that didn&apos;t need to run.</span></li>
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">02</span> <span><b>Compliance alignment · AFT / OCT.</b> Built structural rules ensuring every cross-border and domestic remitter transaction perfectly aligned with current Visa/Mastercard mandates for Account Funding and Original Credit Transactions — eliminating the scheme-penalty exposure and the automated-decline drag that comes with falling out of alignment.</span></li>
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">03</span> <span><b>Data democratization · cost of acceptance, unified.</b> Unified acceptance economics and transaction data into clean cross-departmental dashboards — empowering Customer Service to answer &ldquo;why did this transaction decline?&rdquo; in seconds instead of days, and giving Finance a defensible per-corridor margin view for the first time.</span></li>
          </ol>
        </section>

        {/* ROI BLUEPRINT */}
        <section id="roi" className="scroll-mt-24" data-testid="proof-revperf-roi">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0a1628] via-[#0a1628] to-[#2a1a5e] text-white p-8 lg:p-12">
            <div
              className="absolute inset-0 pointer-events-none opacity-25"
              aria-hidden
              style={{
                background:
                  "radial-gradient(70% 60% at 80% 30%, rgba(172,98,234,0.55) 0%, rgba(11,30,90,0) 70%)",
                filter: "blur(40px)",
              }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/[0.05]">
                <Calculator className="h-3.5 w-3.5 text-[#c9a3f4]" />
                <span className="text-[11px] text-white/85 font-medium uppercase tracking-widest">The ROI blueprint · Beyond tech spend</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl mt-5 tracking-[-0.03em] leading-[1.1] font-semibold">
                The 30% vendor savings is the headline. <span className="text-[#c9a3f4]">The flywheel underneath is the story.</span>
              </h2>
              <p className="text-white/80 mt-5 leading-relaxed max-w-2xl">
                Total project value compounds across four vectors. Each one alone justifies the engagement; together they reset the merchant&apos;s acceptance economics for the long arc.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5" data-testid="proof-revperf-roi-hard">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="h-5 w-5 text-[#c9a3f4] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="label-mono text-white/60">Hard cost savings</div>
                      <div className="font-display text-2xl lg:text-3xl mt-2 font-semibold tracking-tight">30% off PSP + fraud spend</div>
                      <p className="text-xs text-white/65 mt-3 leading-relaxed">Immediate reduction in PSP and fraud-vendor fees from the re-orchestrated call sequence and consolidated coverage. Recurring, not one-off.</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5" data-testid="proof-revperf-roi-topline">
                  <div className="flex items-start gap-3">
                    <Banknote className="h-5 w-5 text-[#c9a3f4] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="label-mono text-white/60">Top-line revenue injection</div>
                      <div className="font-display text-2xl lg:text-3xl mt-2 font-semibold tracking-tight">~$9M recovered volume</div>
                      <p className="text-xs text-white/65 mt-3 leading-relaxed">A 1.5% acceptance-rate increase on the client&apos;s authorization baseline = roughly <b className="text-white">$9M of transactional volume per year</b> that was previously declining at the scheme or fraud layer.</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5 sm:col-span-2" data-testid="proof-revperf-roi-flywheel">
                  <div className="flex items-start gap-3">
                    <Repeat className="h-5 w-5 text-[#c9a3f4] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="label-mono text-white/60">The returning-customer flywheel</div>
                      <div className="font-display text-2xl lg:text-3xl mt-2 font-semibold tracking-tight">1.5x return probability, compounding</div>
                      <p className="text-sm text-white/80 mt-3 leading-relaxed">
                        Historical client data shows every successful payment approval increases the statistical probability of that customer returning by <b className="text-white">1.5x</b>. Recovering roughly $9M of previously-declined volume isn&apos;t just $9M of revenue this year — it&apos;s a cohort of customers <b className="text-white">1.5x more likely</b> to come back and transact again.
                      </p>
                      <p className="text-sm text-white/80 mt-3 leading-relaxed">
                        Translated into operating terms: every prevented first-time decline <b className="text-white">slashes future Customer Acquisition Cost (CAC)</b> for that customer, and lifts the lifetime value (LTV) the merchant ever realizes from them. The compounding effect is multi-year — the year-one impact is the floor, not the ceiling.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5 sm:col-span-2" data-testid="proof-revperf-roi-risk">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 text-[#c9a3f4] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="label-mono text-white/60">Risk mitigation value</div>
                      <div className="font-display text-2xl lg:text-3xl mt-2 font-semibold tracking-tight">Scheme exposure eliminated</div>
                      <p className="text-sm text-white/80 mt-3 leading-relaxed">
                        Bringing the stack into full AFT/OCT alignment removes a category of cost that doesn&apos;t show on a P&amp;L until it lands as a fine or a forced decline — at which point it&apos;s already moved capital. Theoretical capital saved scales with volume; at nine-figure annual movement, the avoided downside is material.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 rounded-xl border border-[#ac62ea]/40 bg-[#ac62ea]/[0.10] p-5">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-[#c9a3f4] flex-shrink-0 mt-0.5" />
                  <p className="text-white/90 leading-relaxed text-sm">
                    <b className="text-white">Hard savings + ~$9M recovered volume + the multi-year LTV flywheel + eliminated scheme exposure</b> — the Revenue Performance Package compounds across all four vectors. For a high-volume regulated remitter, the project pays back inside the first quarter and keeps paying long after.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Operational Transformation */}
        <section id="operational" className="scroll-mt-24" data-testid="proof-revperf-operational">
          <div className="label-mono">Operational transformation</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">Faster disputes. Sharper product calls. Customer Service that knows the answer before the customer asks.</h2>
          <p className="mt-5 text-foreground/85 leading-relaxed text-lg">
            The financial outcome is the headline, but the durable change is operational. With unified data and a re-orchestrated stack, the org started running differently.
          </p>
          <div className="mt-7 grid sm:grid-cols-2 gap-4">
            <div className="card-sharp p-5 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]">
              <div className="flex items-start gap-3">
                <Database className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="label-mono">Finance</div>
                  <p className="mt-2 text-foreground/85 leading-relaxed text-sm">Cost of acceptance, per corridor and per BIN, finally legible. Pricing and renewal conversations newly defensible — no more negotiating in the dark.</p>
                </div>
              </div>
            </div>
            <div className="card-sharp p-5 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]">
              <div className="flex items-start gap-3">
                <LineChart className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="label-mono">Product</div>
                  <p className="mt-2 text-foreground/85 leading-relaxed text-sm">Funnel decline drivers attributable to specific rules, vendors, and orchestration paths — letting product prioritize fixes where they actually move acceptance.</p>
                </div>
              </div>
            </div>
            <div className="card-sharp p-5 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="label-mono">Customer Service</div>
                  <p className="mt-2 text-foreground/85 leading-relaxed text-sm">Dispute and decline triage cut from days to minutes. Frontline reps can see exactly why a transaction declined — and recover the customer in real time.</p>
                </div>
              </div>
            </div>
            <div className="card-sharp p-5 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="label-mono">Compliance & Risk</div>
                  <p className="mt-2 text-foreground/85 leading-relaxed text-sm">AFT/OCT alignment built into the rules layer, not the policy doc — every transaction is structurally compliant by default, not by audit.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final callout */}
        <section id="next" className="scroll-mt-24" data-testid="proof-revperf-cta-section">
          <div className="rounded-2xl border border-border bg-white p-8 lg:p-10">
            <div className="label-mono">What this looks like for you</div>
            <h3 className="font-display text-2xl sm:text-3xl mt-2 tracking-tight font-semibold leading-snug">
              If you process meaningful volume and the answer to &ldquo;what does each transaction actually cost us?&rdquo; lives across three vendors and two teams — that&apos;s the engagement.
            </h3>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Revenue Performance is the package for merchants who&apos;ve outgrown ad-hoc PSP renewals and need a single operator unifying acceptance economics, compliance posture, and conversion lift into one engagement.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-6" data-testid="proof-revperf-final-service">
                <Link to="/services/revenue-performance">
                  Explore Payments Optimization Sprint <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="btn-pill h-11 px-6" data-testid="proof-revperf-final-brief">
                <Link to="/inquiry">Send a custom brief</Link>
              </Button>
              <button
                type="button"
                onClick={() => openChat({ context: "Proof · Revenue Performance case study · Custom inquiry" })}
                className="btn-pill h-11 px-6 rounded-full text-foreground/70 hover:text-foreground text-sm font-medium underline-offset-4 hover:underline"
                data-testid="proof-revperf-final-chat"
              >
                Or chat with us &rarr;
              </button>
            </div>
          </div>
        </section>

        <CaseStudyFaq testid="proof-revperf-faq" schemaId="revenue-performance" items={FAQS} />

        {/* Cross-link */}
        <div className="text-center pt-4" data-testid="proof-revperf-cross-link">
          <Link
            to="/proof/rxp"
            className="inline-flex items-center gap-1.5 text-sm text-[#ac62ea] hover:text-[#8a48cf] font-medium"
          >
            Next case study: Procurement / RFP Project — 4 card PSPs to 1 &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
}
